import {
  FlatTableRow,
  FlatTableCell,
  FlatTableCheckbox,
} from 'carbon-react/esm/components/flat-table';
import Pill from 'carbon-react/esm/components/pill';
import { useNavigate } from 'react-router-dom';
import { Dsn, type StatusConfig } from '../../../types/dsn.types';
import { formatDateString, formatTimeString } from '../../../utils/dateFormatters';
import {
  mapNatureToDisplay,
  mapStatusUIItem,
  mapTypeLabelToDisplay,
} from '../../../utils/dsnMappings';
import { DsnRowActions } from './DsnRowActions';
import { saveDsnListReturnState } from '../../../utils/dsnListReturnState';

const renderPeriod = (period: string) => {
  const [startDate, endDate] = period.split(/\s+au\s+/i);

  if (!endDate) {
    return period;
  }

  return (
    <>
      <span style={{ display: 'block' }}>Du {startDate}</span>
      <span style={{ display: 'block', color: 'var(--colorsUtilityYin065)' }}>au {endDate}</span>
    </>
  );
};

const renderTwoLineValue = (value: string) => {
  const normalizedValue = value.trim();
  const lastSpaceIndex = normalizedValue.lastIndexOf(' ');

  if (lastSpaceIndex <= 0 || lastSpaceIndex === normalizedValue.length - 1) {
    return value;
  }

  return (
    <span aria-label={normalizedValue}>
      <span style={{ display: 'block', overflowWrap: 'break-word' }}>
        {normalizedValue.slice(0, lastSpaceIndex)}
      </span>
      <span
        style={{
          display: 'block',
          color: 'var(--colorsUtilityYin065)',
          overflowWrap: 'break-word',
        }}
      >
        {normalizedValue.slice(lastSpaceIndex + 1)}
      </span>
    </span>
  );
};

const getStatusTitle = (statusInfos: string, fallback: string) => {
  const statusTitle = statusInfos
    .replace(
      /\s*\/\s*(?:\d{14}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\s*$/i,
      '',
    )
    .trim();

  return statusTitle || fallback;
};

const StatusPill = ({ label, pillVariant }: Pick<StatusConfig, 'label' | 'pillVariant'>) => {
  const pill = (
    // carbon-react incorrectly deprecates the whole size prop; S is still the intended compact size.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    <Pill variant={pillVariant} size="S">
      {label}
    </Pill>
  );

  return pill;
};

interface DsnTableRowProps {
  item: Dsn;
  isHighlighted: boolean;
  isActive: boolean;
  currentPage: number;
  pageSize: number;
  selectedRows: Record<string, boolean>;
  handleHighlightRow: (id: string) => void;
  handleSelectRow: (id: string) => void;
  setHighlightedRow: (id: string) => void;
  setActiveRow: (id: string) => void;
  handleDownload: (id: string, canDownload: boolean) => void;
  handleViewDeclaration: (id: string, canView: boolean) => void;
  downloadLoading: boolean;
  aerBilanLoading: boolean;
}

export const DsnTableRow = ({
  item,
  isHighlighted,
  isActive,
  currentPage,
  pageSize,
  selectedRows,
  handleHighlightRow,
  handleSelectRow,
  setHighlightedRow,
  setActiveRow,
  handleDownload,
  handleViewDeclaration,
  downloadLoading,
  aerBilanLoading,
}: DsnTableRowProps) => {
  const navigate = useNavigate();

  const openDsnDetails = () => {
    setActiveRow(item.InstanceId);
    saveDsnListReturnState(item.InstanceId, currentPage, pageSize);
    void navigate(`/dsn-list/${item.InstanceId}`);
  };

  const handleDepositedAtClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // clic droit / molette / ctrl+clic / cmd+clic : laisser le navigateur gérer nativement
    // (ex: ouvrir dans un nouvel onglet) plutôt que de forcer la navigation client-side
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    openDsnDetails();
  };

  const statusUIItem = mapStatusUIItem(item.Status);
  const statusTitle = getStatusTitle(item.StatutInfos, statusUIItem.label);

  return (
    <FlatTableRow
      highlighted={isActive || isHighlighted}
      data-role={isActive ? 'dsn-table-row-active' : undefined}
    >
      {!item.DisplayPdfImg && !item.DisplayZipImg ? (
        <FlatTableCell>
          <div
            style={{
              width: '16px',
              height: '16px',
              border: '1px solid #ccc',
              borderRadius: '2px',
              backgroundColor: '#f5f5f5',
              opacity: 0.5,
            }}
          />
        </FlatTableCell>
      ) : (
        <FlatTableCheckbox
          checked={selectedRows[item.InstanceId] || false}
          onChange={e => {
            e.stopPropagation();
            handleSelectRow(item.InstanceId);
          }}
        />
      )}
      <FlatTableCell title={statusTitle}>
        <StatusPill label={statusUIItem.label} pillVariant={statusUIItem.pillVariant} />
      </FlatTableCell>
      <FlatTableCell>{mapNatureToDisplay(item.Nature)}</FlatTableCell>
      <FlatTableCell>{mapTypeLabelToDisplay(item.CodeTypeDeclaration)}</FlatTableCell>
      <FlatTableCell
        data-role="dsn-raison-sociale-cell"
        title={item.DsnTiersDeclareObj?.RaisonSociale ?? ''}
        truncate={false}
      >
        {item.DsnTiersDeclareObj?.RaisonSociale ?? ''}
      </FlatTableCell>
      <FlatTableCell>{item.Etablissement}</FlatTableCell>
      <FlatTableCell>{renderTwoLineValue(item.SalarieArretTravail)}</FlatTableCell>
      <FlatTableCell>{renderPeriod(item.Periode)}</FlatTableCell>
      <FlatTableCell>
        <a
          href={`/dsn-list/${item.InstanceId}`}
          className={isActive ? 'table-link table-link--active' : 'table-link'}
          aria-label={`Voir le détail de la DSN pour la ligne déposée le ${formatDateString(item.DateCreationInterchange)}`}
          onClick={handleDepositedAtClick}
          onKeyDown={e => {
            const isSpaceKey =
              e.code === 'Space' || e.key === ' ' || e.key === 'Space' || e.key === 'Spacebar';

            if (isSpaceKey) {
              e.preventDefault();
              openDsnDetails();
            }
          }}
        >
          {formatDateString(item.DateCreationInterchange)}
        </a>
      </FlatTableCell>
      <FlatTableCell>
        <span style={{ display: 'block' }}>{formatDateString(item.DateSuivi)}</span>
        <span style={{ display: 'block', color: 'var(--colorsUtilityYin065)' }}>
          {formatTimeString(item.DateSuivi)}
        </span>
      </FlatTableCell>
      <FlatTableCell>
        <DsnRowActions
          instanceId={item.InstanceId}
          isHighlighted={isHighlighted}
          displayPdfImg={item.DisplayPdfImg}
          displayZipImg={item.DisplayZipImg}
          handleHighlightRow={id => {
            handleHighlightRow(id);
          }}
          setHighlightedRow={setHighlightedRow}
          handleDownload={handleDownload}
          handleViewDeclaration={handleViewDeclaration}
          downloadLoading={downloadLoading}
          aerBilanLoading={aerBilanLoading}
        />
      </FlatTableCell>
    </FlatTableRow>
  );
};
